import { useEffect, useState } from 'react';
import ClockIcon from '@/ui/icons/ClockIcon';

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

// Local date *and* time of the deadline, so a creator reads the close moment in
// their own timezone rather than just a bare date.
export const formatDeadlineCountdown = (deadline: string, nowMs: number): string => {
    const deadlineMs = new Date(deadline).getTime();
    const diffMs = deadlineMs - nowMs;
    const closesAt = new Date(deadline).toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });

    if (diffMs <= 0) {
        return `Closed ${closesAt}`;
    }

    const days = Math.floor(diffMs / DAY_MS);
    if (days >= 1) {
        return `${days} day${days === 1 ? '' : 's'} left · closes ${closesAt}`;
    }

    const hours = Math.floor(diffMs / HOUR_MS);
    if (hours >= 1) {
        return `${hours} hour${hours === 1 ? '' : 's'} left · closes ${closesAt}`;
    }

    const minutes = Math.max(1, Math.floor(diffMs / MINUTE_MS));
    return `${minutes} min left · closes ${closesAt}`;
};

type Props = {
    deadline: string | null;
    isOpen: boolean;
};

/** Deadline chip shared by the invite and public campaign pages. Refreshes each
 *  minute so the time remaining stays current. */
const DeadlinePill = ({ deadline, isOpen }: Props) => {
    const [nowMs, setNowMs] = useState(() => Date.now());

    useEffect(() => {
        const intervalId = window.setInterval(() => setNowMs(Date.now()), MINUTE_MS);
        return () => window.clearInterval(intervalId);
    }, []);

    if (!isOpen) {
        return (
            <span className="wm-chip">
                <ClockIcon />
                <span>Submissions closed</span>
            </span>
        );
    }

    if (!deadline) {
        return (
            <span className="wm-chip">
                <ClockIcon />
                <span>Open for submissions</span>
            </span>
        );
    }

    return (
        <span className="wm-chip">
            <ClockIcon />
            <span>{formatDeadlineCountdown(deadline, nowMs)}</span>
        </span>
    );
};

export default DeadlinePill;
